import React, { Component } from "react";
import axios from "axios";
import cookie from "react-cookies";
import {
  Navbar,
  NavbarBrand,
  Nav,
  NavItem,
  NavLink,
  Button,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import constants from "../../Constants";
import { saveMediaAccessCookie } from "../../utils/mediaAccessCookie";
import { writeToLocalStorage } from "../../localStorage.js";
import OrderSearch from "../search/OrderSearch";
import "./drawer.css";

const baseURL = constants.baseURL;

const ACCOUNT_MENU_ITEMS = [
  { href: "/profile", label: "Profile", icon: "mdi-account-outline" },
  { href: "/team", label: "Team", icon: "mdi-account-group-outline" },
  { href: "/billing", label: "Trip billing", icon: "mdi-receipt" },
  { href: "/settings", label: "Settings", icon: "mdi-cog-outline" },
];

const DRAWER_SECTIONS = [
  {
    items: [
      { href: "/dashboard", label: "Dashboard", icon: "mdi-view-dashboard-outline" },
    ],
  },
  {
    label: "Developers",
    items: [
      {
        href: "/integrations",
        label: "Integrations",
        icon: "mdi-link-variant",
        match: ["/integrations", "/widget", "/developers"],
      },
      {
        href: "/partner-plans",
        label: "API plan",
        icon: "mdi-package-variant",
        showPlanBadge: true,
      },
      {
        href: "/partner-api-docs",
        label: "API documentation",
        icon: "mdi-book-open-variant",
        match: ["/partner-api-docs", "/api-docs", "/enterprise-api-docs", "/docs"],
      },
    ],
  },
  {
    label: "Account",
    items: [
      { href: "/profile", label: "Profile", icon: "mdi-account-outline" },
      { href: "/team", label: "Team", icon: "mdi-account-group-outline" },
      { href: "/billing", label: "Trip billing", icon: "mdi-receipt" },
    ],
  },
];

const GUEST_LINKS = [
  { href: "/login", label: "Log in", icon: "mdi-login" },
  { href: "/signup", label: "Sign up", icon: "mdi-account-plus-outline" },
  { href: "/about", label: "About us", icon: "mdi-information-outline" },
  { href: "/howitworks", label: "How it works", icon: "mdi-play-circle-outline" },
  { href: "/contact", label: "Contact", icon: "mdi-email-outline" },
];

function pathMatches(href, pathname, match) {
  if (match && match.length) {
    return match.some((p) => pathname === p || pathname.startsWith(p + "/"));
  }
  if (href === "/dashboard") {
    return pathname === "/dashboard" || pathname === "/";
  }
  return pathname === href || pathname.startsWith(href + "/");
}

function userInitial(name) {
  const n = String(name || "").trim();
  if (!n) return "?";
  return n.charAt(0).toUpperCase();
}

function formatPlanLabel(plan) {
  if (!plan || plan === "sandbox") return "Sandbox";
  return String(plan).replace(/_/g, " ");
}

class NavbarPage extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isDrawerOpen: false,
      isAccountMenuOpen: false,
      isLogoutModalOpen: false,
      currentPath: typeof window !== "undefined" ? window.location.pathname : "/",
      partnerApiPlan: null,
      partnerApiActivated: false,
      canManageIntegrations: null,
      canBookTrips: true,
    };
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handlePathChange = this.handlePathChange.bind(this);
  }

  toggleDrawer = () => {
    const opening = !this.state.isDrawerOpen;
    this.setState({ isDrawerOpen: opening, isAccountMenuOpen: false }, () => {
      document.body.style.overflow = opening ? "hidden" : "auto";
      if (
        opening &&
        cookie.load("session-id-qt") &&
        this.state.canManageIntegrations === true &&
        this.state.partnerApiPlan === null
      ) {
        this.fetchPartnerPlanSummary();
      }
    });
  };

  closeDrawer = () => {
    this.setState({ isDrawerOpen: false });
    document.body.style.overflow = "auto";
  };

  toggleAccountMenu = () => {
    this.setState((prev) => ({
      isAccountMenuOpen: !prev.isAccountMenuOpen,
      isDrawerOpen: false,
    }));
  };

  closeAccountMenu = () => {
    this.setState({ isAccountMenuOpen: false });
  };

  openLogoutModal = (e) => {
    if (e) e.preventDefault();
    this.setState({
      isLogoutModalOpen: true,
      isAccountMenuOpen: false,
      isDrawerOpen: false,
    });
    document.body.style.overflow = "auto";
  };

  closeLogoutModal = () => {
    this.setState({ isLogoutModalOpen: false });
  };

  confirmLogout = () => {
    this.setState({ isLogoutModalOpen: false });
    window.location.href = "/logout";
  };

  handleKeyDown(e) {
    if (e.key === "Escape") {
      if (this.state.isLogoutModalOpen) {
        this.closeLogoutModal();
      } else if (this.state.isAccountMenuOpen) {
        this.closeAccountMenu();
      } else if (this.state.isDrawerOpen) {
        this.closeDrawer();
      }
    }
  }

  handlePathChange() {
    this.setState({
      currentPath: window.location.pathname,
      isAccountMenuOpen: false,
    });
  }

  componentDidMount() {
    if (cookie.load("session-id-qt")) {
      this.getCustomerProfile();
    }
    window.addEventListener("keydown", this.handleKeyDown);
    window.addEventListener("popstate", this.handlePathChange);
    window.addEventListener("qt-nav-path", this.handlePathChange);
  }

  componentWillUnmount() {
    document.body.style.overflow = "auto";
    window.removeEventListener("keydown", this.handleKeyDown);
    window.removeEventListener("popstate", this.handlePathChange);
    window.removeEventListener("qt-nav-path", this.handlePathChange);
  }

  fetchPartnerPlanSummary() {
    if (this.state.canManageIntegrations !== true) {
      this.setState({ partnerApiPlan: null, partnerApiActivated: false });
      return;
    }
    axios
      .get(baseURL + "/partner/portal/summary?days=7", {
        headers: {
          "Content-Type": "application/json",
          Authorization: cookie.load("session-id-qt"),
        },
      })
      .then((res) => {
        if (res.status !== 200 || !res.data) return;
        const access = res.data.access;
        const plan =
          res.data.plan_billing?.current_plan || res.data.entitlements?.plan || "starter";
        const activated = !!(access?.hq_provisioned && !access?.partner_api_suspended);
        this.setState({
          partnerApiPlan: activated ? plan : null,
          partnerApiActivated: activated,
        });
      })
      .catch(() => {
        this.setState({ partnerApiPlan: null, partnerApiActivated: false });
      });
  }

  getCustomerProfile() {
    axios
      .get(baseURL + "/customer-profile", {
        headers: {
          "Content-Type": "application/json",
          Authorization: cookie.load("session-id-qt"),
        },
      })
      .then((response) => {
        if (response.status === 200) {
          if (response.data.message && response.data.message.length > 0) {
            this.setState({ message: response.data.message });
          } else if (
            response.data.firstname &&
            response.data.customerid &&
            (response.data.promocode || response.data.hasTeam || response.data.teamRole)
          ) {
            cookie.save("ctName", response.data.firstname, {
              path: "/",
              maxAge: 86400,
              secure: true,
            });
            cookie.save("localsessionid", response.data.customerid, {
              path: "/",
              maxAge: 86400,
              secure: true,
            });
            cookie.save("promocode", response.data.promocode, {
              path: "/",
              maxAge: 86400,
              secure: true,
            });
            saveMediaAccessCookie(cookie.load("session-id-qt"));

            const stats = response.data.stats || {};
            writeToLocalStorage("quotations", stats.quotations || 0);
            writeToLocalStorage("orders", stats.orders || 0);
            writeToLocalStorage("completed", stats.completed || 0);
            writeToLocalStorage("processing", stats.processing || 0);
            writeToLocalStorage("storagehours", stats.storagehours || 0);
            writeToLocalStorage("cancelled", stats.cancelled || 0);
            writeToLocalStorage("totalcost", stats.totalcost || 0);
            writeToLocalStorage("totaldistance", stats.totaldistance || 0);
            writeToLocalStorage("storages", stats.storages || 0);
            writeToLocalStorage("conversations", stats.conversations || 0);
            writeToLocalStorage("vehicles", stats.vehicles || 0);
            writeToLocalStorage("totalcostpaid", stats.totalcostpaid || 0);
            const canManageIntegrations = response.data.canManageIntegrations !== false;
            this.setState(
              {
                canManageIntegrations,
                canBookTrips: response.data.canBookTrips !== false,
              },
              () => {
                if (canManageIntegrations) {
                  this.fetchPartnerPlanSummary();
                }
              }
            );
          }
        }
      });
  }

  bookNewTrip = () => {
    this.closeDrawer();
    this.closeAccountMenu();
    try {
      if (window.location.pathname.startsWith("/dashboard")) {
        window.dispatchEvent(new CustomEvent("qt-book-new-trip"));
      } else {
        window.location.href = "/dashboard?booknew=1";
      }
    } catch (_e) {
      window.location.href = "/dashboard";
    }
  };

  renderDrawerLink(item) {
    const { currentPath, partnerApiPlan } = this.state;
    const active = pathMatches(item.href, currentPath, item.match);
    const badge =
      item.showPlanBadge && partnerApiPlan ? formatPlanLabel(partnerApiPlan) : null;

    return (
      <NavItem key={item.href}>
        <NavLink
          href={item.href}
          className={"drawer-nav-link" + (active ? " is-active" : "")}
          onClick={this.closeDrawer}
        >
          <span className="drawer-nav-icon">
            <i className={"mdi " + item.icon} aria-hidden="true" />
          </span>
          <span className="drawer-nav-label">{item.label}</span>
          {badge ? <span className="drawer-nav-badge">{badge}</span> : null}
        </NavLink>
      </NavItem>
    );
  }

  renderDrawerHeader(loggedIn) {
    if (!loggedIn) {
      return (
        <div className="drawer-header">
          <button type="button" className="drawer-close" onClick={this.closeDrawer} aria-label="Close menu">
            ×
          </button>
          <h2 className="drawer-guest-title">QikTruck Business</h2>
          <p className="drawer-guest-sub">Book, track, and integrate deliveries</p>
        </div>
      );
    }

    const name = cookie.load("ctName") || "Account";
    const promocode = cookie.load("promocode");
    const { partnerApiPlan, partnerApiActivated } = this.state;

    return (
      <div className="drawer-header">
        <button type="button" className="drawer-close" onClick={this.closeDrawer} aria-label="Close menu">
          ×
        </button>
        <div className="drawer-user">
          <div className="drawer-avatar" aria-hidden="true">
            {userInitial(name)}
          </div>
          <div className="drawer-user-meta">
            <h2 className="drawer-user-name">{name}</h2>
            <p className="drawer-user-sub">
              {promocode ? (
                <img
                  src={"https://media.qiktruck.co.za/bookfiles/" + promocode + "-logo.png"}
                  alt=""
                />
              ) : null}
              <span>Business portal</span>
            </p>
          </div>
        </div>
        {partnerApiActivated && partnerApiPlan ? (
          <a href="/partner-plans" className="drawer-plan-pill" onClick={this.closeDrawer}>
            <i className="mdi mdi-package-variant" aria-hidden="true" />
            API plan: {formatPlanLabel(partnerApiPlan)}
          </a>
        ) : null}
      </div>
    );
  }

  getDrawerSections() {
    const { canManageIntegrations } = this.state;
    if (canManageIntegrations) return DRAWER_SECTIONS;
    return DRAWER_SECTIONS.map((section) => {
      if (section.label !== "Developers") return section;
      return {
        ...section,
        items: section.items.filter(
          (item) =>
            !item.href.startsWith("/integrations") && item.href !== "/partner-plans"
        ),
      };
    });
  }

  renderAccountMenu() {
    const { isAccountMenuOpen, currentPath } = this.state;
    const name = cookie.load("ctName") || "Account";
    const promocode = cookie.load("promocode");

    return (
      <Dropdown
        isOpen={isAccountMenuOpen}
        toggle={this.toggleAccountMenu}
        className="qt-account-dropdown"
      >
        <DropdownToggle
          tag="button"
          type="button"
          className="qt-account-toggle"
          aria-label="Account menu"
          aria-expanded={isAccountMenuOpen}
        >
          <span className="qt-account-avatar" aria-hidden="true">
            {userInitial(name)}
          </span>
          <span className="qt-account-name d-none d-sm-inline">{name}</span>
          <i
            className={
              "mdi " + (isAccountMenuOpen ? "mdi-chevron-up" : "mdi-chevron-down")
            }
            aria-hidden="true"
          />
        </DropdownToggle>
        <DropdownMenu end className="qt-account-menu">
          <div className="qt-account-menu-head">
            <strong>{name}</strong>
            {promocode ? <span className="qt-account-menu-promo">{promocode}</span> : null}
          </div>
          <DropdownItem divider />
          {ACCOUNT_MENU_ITEMS.map((item) => (
            <DropdownItem
              key={item.href}
              href={item.href}
              className={
                "qt-account-menu-item" +
                (pathMatches(item.href, currentPath) ? " is-active" : "")
              }
              onClick={this.closeAccountMenu}
            >
              <i className={"mdi " + item.icon} aria-hidden="true" />
              {item.label}
            </DropdownItem>
          ))}
          <DropdownItem divider />
          <DropdownItem
            tag="button"
            type="button"
            className="qt-account-menu-item is-danger"
            onClick={this.openLogoutModal}
          >
            <i className="mdi mdi-logout" aria-hidden="true" />
            Log out
          </DropdownItem>
        </DropdownMenu>
      </Dropdown>
    );
  }

  renderLogoutModal() {
    const { isLogoutModalOpen } = this.state;
    const name = cookie.load("ctName");

    return (
      <Modal
        isOpen={isLogoutModalOpen}
        toggle={this.closeLogoutModal}
        centered
        className="qt-logout-modal"
      >
        <ModalHeader toggle={this.closeLogoutModal}>Log out?</ModalHeader>
        <ModalBody>
          <p className="mb-0">
            {name
              ? `Are you sure you want to log out of ${name}'s QikTruck Business account?`
              : "Are you sure you want to log out of your QikTruck Business account?"}
          </p>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={this.closeLogoutModal}>
            Stay signed in
          </Button>
          <Button color="danger" onClick={this.confirmLogout}>
            <i className="mdi mdi-logout me-1" aria-hidden="true" />
            Log out
          </Button>
        </ModalFooter>
      </Modal>
    );
  }

  render() {
    localStorage.removeItem("notificationData");
    const { isDrawerOpen, currentPath, canManageIntegrations, canBookTrips } = this.state;
    const loggedIn = !!cookie.load("session-id-qt");

    return (
      <React.Fragment>
        <div className="qt-nav-drawer">
          {isDrawerOpen && (
            <div className="drawer-overlay" onClick={this.closeDrawer} aria-hidden="true" />
          )}

          <Navbar className={"navbar-custom " + this.props.navClass}>
            <div className="d-flex justify-content-between align-items-center w-100" style={{ gap: 12 }}>
              <div className="d-flex align-items-center" style={{ flexShrink: 0 }}>
                <button
                  type="button"
                  className="drawer-toggle"
                  onClick={this.toggleDrawer}
                  aria-label={isDrawerOpen ? "Close menu" : "Open menu"}
                  aria-expanded={isDrawerOpen}
                >
                  <i className={"mdi " + (isDrawerOpen ? "mdi-close" : "mdi-menu")} aria-hidden="true" />
                </button>

                <NavbarBrand className="logo ms-3" href="/dashboard">
                  <b className="qt-brand-text" style={{ fontSize: "20px", color: "white" }}>
                    {"QIKTRUCK".split("").map((ch, i) => (
                      <span
                        key={i}
                        className="qt-brand-letter"
                        style={{ animationDelay: `${i * 90}ms` }}
                      >
                        {ch}
                      </span>
                    ))}
                  </b>
                </NavbarBrand>
                {loggedIn && cookie.load("promocode") ? (
                  <img
                    src={
                      "https://media.qiktruck.co.za/bookfiles/" +
                      cookie.load("promocode") +
                      "-logo.png"
                    }
                    className="partner-logo"
                    alt=""
                    height="20"
                  />
                ) : null}
              </div>

              {loggedIn && (
                <div
                  className="navbar-order-search d-none d-md-block"
                  style={{ flex: 1, maxWidth: 520, margin: "0 16px" }}
                >
                  <OrderSearch />
                </div>
              )}

              <div className="d-flex align-items-center" style={{ flexShrink: 0, gap: 10 }}>
                {loggedIn && canManageIntegrations && (
                  <a
                    href="/integrations"
                    className="text-white d-none d-md-inline"
                    style={{ fontSize: 13, textDecoration: "none", whiteSpace: "nowrap" }}
                    title="Connect your store - widget, API, Shopify, WooCommerce and more"
                  >
                    <i className="mdi mdi-link-variant" />
                    <span className="d-none d-lg-inline">&nbsp;Integrations</span>
                  </a>
                )}
                {loggedIn && canManageIntegrations && (
                  <a
                    href="/partner-plans"
                    className="text-white d-none d-lg-inline"
                    style={{ fontSize: 13, textDecoration: "none", whiteSpace: "nowrap" }}
                    title="Partner API plan, quotas, and subscription for your account"
                  >
                    <i className="mdi mdi-package-variant" />
                    <span>&nbsp;API plan</span>
                  </a>
                )}
                {loggedIn && canBookTrips && (
                  <button
                    type="button"
                    className="book-again-btn navbar-book-trip"
                    onClick={this.bookNewTrip}
                    title="Start a brand new trip booking"
                  >
                    <i className="mdi mdi-plus-circle-outline" />
                    <span className="d-none d-sm-inline">&nbsp;Book new trip</span>
                  </button>
                )}
                {loggedIn ? this.renderAccountMenu() : null}

                {!loggedIn && (
                  <div className="d-flex">
                    <Button color="primary" className="me-2" href="/login">
                      Login
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </Navbar>

          <nav
            className={"drawer-menu" + (isDrawerOpen ? " open" : "")}
            aria-label="Main navigation"
            aria-hidden={!isDrawerOpen}
          >
            {this.renderDrawerHeader(loggedIn)}

            <div className="drawer-content">
              {loggedIn ? (
                <Nav vertical className="drawer-nav">
                  {this.getDrawerSections().map((section, idx) => (
                    <div className="drawer-section" key={section.label || "main-" + idx}>
                      {section.label ? <h3 className="drawer-heading">{section.label}</h3> : null}
                      {section.items.map((item) => this.renderDrawerLink(item))}
                    </div>
                  ))}
                </Nav>
              ) : (
                <Nav vertical className="drawer-nav">
                  {GUEST_LINKS.map((item) => (
                    <NavItem key={item.href}>
                      <NavLink
                        href={item.href}
                        className={
                          "drawer-nav-link" +
                          (pathMatches(item.href, currentPath) ? " is-active" : "")
                        }
                        onClick={this.closeDrawer}
                      >
                        <span className="drawer-nav-icon">
                          <i className={"mdi " + item.icon} aria-hidden="true" />
                        </span>
                        <span className="drawer-nav-label">{item.label}</span>
                      </NavLink>
                    </NavItem>
                  ))}
                </Nav>
              )}
            </div>

            {loggedIn ? (
              <div className="drawer-footer">
                {canBookTrips ? (
                  <button type="button" className="drawer-cta" onClick={this.bookNewTrip}>
                    <i className="mdi mdi-plus-circle-outline" aria-hidden="true" />
                    Book new trip
                  </button>
                ) : null}
                <button
                  type="button"
                  className="drawer-cta-secondary is-danger"
                  onClick={this.openLogoutModal}
                >
                  <i className="mdi mdi-logout" aria-hidden="true" />
                  Log out
                </button>
              </div>
            ) : (
              <div className="drawer-footer">
                <a href="/login" className="drawer-cta" onClick={this.closeDrawer}>
                  <i className="mdi mdi-login" aria-hidden="true" />
                  Log in to dashboard
                </a>
              </div>
            )}
          </nav>
        </div>

        {this.renderLogoutModal()}
      </React.Fragment>
    );
  }
}

export default NavbarPage;
